import React, { useState, useEffect, useCallback } from 'react';
import { Icon, Modal, Select, SelectItem } from '../components/ui';
import ShadowAiGraph from '../components/ShadowAiGraph';
import { listAllProviders, markEndpointNotAi, confirmEndpointAi, type CatalogProvider, type RiskLevel } from '../lib/providerCatalogApi';
import { useTranslation } from 'react-i18next';

interface DiscoveryProps {
  apiUrl: string;
}

interface Summary {
  mcp_servers: { managed: number; unmanaged: number };
  agents: { shadow: number; managed: number };
  endpoints: { llm: number; mcp: number };
  shadow_ai?: { shadow: number; protected: number };
}

interface DiscoveredAgent {
  disc_id: string;
  source_key: string;
  source_ip?: string;
  source_host?: string;
  identity_hint?: string;
  dest_endpoints: string;
  endpoint_types: string;
  request_count: number;
  status: 'shadow' | 'managed' | 'ignored';
  source: string;
  last_seen: string;
  // Operator-chosen name, set when the agent is brought under ZTAI.
  display_name?: string | null;
  // Endpoint (BDR agent) attribution — present when source === 'bdr-agent'.
  process_name?: string | null;
  process_path?: string | null;
  device_os?: string | null;
}

interface DiscoveredEndpoint {
  endpoint: string;
  provider: string;
  type: 'llm' | 'mcp';
  internal: number;
  management_status: string;
  hit_count: number;
  classification?: 'shadow_ai' | 'ztai_protected' | null;
  inferred_model?: string | null;
  last_seen: string;
  riskLevel?: 'sanctioned' | 'tolerated' | 'unsanctioned' | 'high_risk';
  source?: string;
}

type McpAuthType = 'none' | 'basic' | 'bearer_token' | 'api_key' | 'jwt' | 'oauth2';

const RISK_BADGE: Record<string, string> = {
  sanctioned: 'badge-success',
  tolerated: 'badge-info',
  unsanctioned: 'badge-unknown',
  high_risk: 'badge-error',
};

const RISK_LABEL: Record<string, string> = {
  sanctioned: 'Sanctioned',
  tolerated: 'Tolerated',
  unsanctioned: 'Unsanctioned',
  high_risk: 'High Risk',
};

interface MCPServerRow {
  server_id: string;
  name: string;
  hostname: string;
  port: number;
  health_status: string;
  source?: string;
  management_status?: string;
}

interface IngestStats {
  queue: { queueDepth: number; received: number; dropped: number; processed: number; enabled: boolean };
  syslog: { received: number; parsed: number; errors: number };
}

const STATUS_BADGE: Record<string, string> = {
  shadow: 'badge-error',
  managed: 'badge-success',
  ignored: 'badge-unknown',
  unmanaged: 'badge-error',
};

const STATUS_LABEL_KEY: Record<string, string> = {
  shadow: 'pages.discovery.statusShadow',
  managed: 'pages.discovery.statusManaged',
  ignored: 'pages.discovery.statusIgnored',
  unmanaged: 'pages.discovery.statusUnmanaged',
};

interface SpendByProvider {
  provider: string;
  event_count: number;
  estimated_prompt_tokens: number;
  estimated_cost_usd: number;
}

interface SpendEstimate {
  configured: boolean;
  message?: string;
  generated_at?: string;
  caveat?: string;
  total_estimated_cost_usd: number;
  by_provider: SpendByProvider[];
}

interface BomEntry {
  vendor: string;
  service: string;
  type: 'llm' | 'mcp';
  risk_level?: string;
  management_status: string;
  classification?: string | null;
  inferred_model?: string | null;
  first_seen: string;
  last_seen: string;
  hit_count: number;
}

type Tab = 'agents' | 'mcp' | 'endpoints' | 'graph' | 'bom';

const Discovery: React.FC<DiscoveryProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [agents, setAgents] = useState<DiscoveredAgent[]>([]);
  const [endpoints, setEndpoints] = useState<DiscoveredEndpoint[]>([]);
  const [servers, setServers] = useState<MCPServerRow[]>([]);
  const [tab, setTab] = useState<Tab>('agents');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // Agent awaiting adoption — held here so we can ask for a name first.
  const [adopting, setAdopting] = useState<DiscoveredAgent | null>(null);
  const [adoptName, setAdoptName] = useState('');

  // Endpoint awaiting a "Confirm AI" verdict — provider list is lazy-loaded the
  // first time this modal opens (Discovery doesn't otherwise need the catalog).
  const [confirmingAi, setConfirmingAi] = useState<DiscoveredEndpoint | null>(null);
  const [confirmProviders, setConfirmProviders] = useState<CatalogProvider[]>([]);
  const [confirmProviderId, setConfirmProviderId] = useState('');
  const [confirmRiskLevel, setConfirmRiskLevel] = useState<RiskLevel>('unsanctioned');

  const [showScan, setShowScan] = useState(false);
  const [scanHosts, setScanHosts] = useState('localhost');
  const [scanPorts, setScanPorts] = useState('3001,3002,3003');
  const [scanning, setScanning] = useState(false);

  const [showIngest, setShowIngest] = useState(false);
  const [ingestFormat, setIngestFormat] = useState('squid');
  const [ingestText, setIngestText] = useState('');
  const [ingesting, setIngesting] = useState(false);

  const [showAddMcpModal, setShowAddMcpModal] = useState(false);
  const [selectedEndpoint, setSelectedEndpoint] = useState<DiscoveredEndpoint | null>(null);
  const [mcpFormData, setMcpFormData] = useState({ name: '', hostname: '', port: 3000, protocol: 'http' });
  const [authType, setAuthType] = useState<McpAuthType>('bearer_token');
  const [showAdvancedAuth, setShowAdvancedAuth] = useState(false);
  const [authFields, setAuthFields] = useState({
    token: '', username: '', password: '', // basic / bearer_token / api_key
    jwtSecret: '', jwtAlgorithm: 'HS256' as 'HS256' | 'RS256', // jwt
    clientId: '', clientSecret: '', tokenUrl: '', scope: '', // oauth2
  });
  const [onboarding, setOnboarding] = useState(false);
  const [onboardError, setOnboardError] = useState<string | null>(null);

  const [toast, setToast] = useState<string | null>(null);
  const [stats, setStats] = useState<IngestStats | null>(null);

  const [spend, setSpend] = useState<SpendEstimate | null>(null);
  const [bomEntries, setBomEntries] = useState<BomEntry[]>([]);
  const [bomLoading, setBomLoading] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, a, e, m, st] = await Promise.all([
        fetch(`${apiUrl}/admin/discovery/summary`).then((r) => r.json()),
        fetch(`${apiUrl}/admin/discovery/agents`).then((r) => r.json()),
        fetch(`${apiUrl}/admin/discovery/endpoints`).then((r) => r.json()),
        fetch(`${apiUrl}/admin/mcp/servers?limit=200`).then((r) => r.json()),
        fetch(`${apiUrl}/admin/discovery/stats`).then((r) => r.json()),
      ]);
      setSummary(s);
      setAgents(a.agents || []);
      setEndpoints(e.endpoints || []);
      setServers(m.servers || []);
      setStats(st);
    } catch (err: any) {
      setError(`Cannot reach discovery API: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (tab !== 'bom') return;
    setBomLoading(true);
    Promise.all([
      fetch(`${apiUrl}/admin/discovery/shadow-ai-spend`).then((r) => r.json()),
      fetch(`${apiUrl}/admin/discovery/ai-bom`).then((r) => r.json()),
    ])
      .then(([spendRes, bomRes]) => {
        setSpend(spendRes);
        setBomEntries(bomRes.entries || []);
      })
      .catch(() => {})
      .finally(() => setBomLoading(false));
  }, [tab, apiUrl]);

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  // Authenticated file download: plain <a href download> can't carry the
  // Bearer token this API requires, so fetch (which the app's global fetch
  // override stamps with Authorization) + blob + a synthetic click instead.
  const downloadAuthed = async (path: string, filename: string) => {
    try {
      const res = await fetch(`${apiUrl}${path}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || `Download failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      flash(err.message || 'Download failed');
    }
  };

  const toggleIngestion = async () => {
    try {
      const endpoint = stats?.queue.enabled ? '/admin/discovery/disable' : '/admin/discovery/enable';
      await fetch(`${apiUrl}${endpoint}`, { method: 'POST' }).then((r) => r.json());
      flash(stats?.queue.enabled ? 'Log ingestion paused' : 'Log ingestion resumed');
      await loadAll();
    } catch (err: any) {
      flash(`Toggle failed: ${err.message}`);
    }
  };

  const openAddMcpModal = (endpoint: DiscoveredEndpoint) => {
    setSelectedEndpoint(endpoint);
    setMcpFormData({
      name: `${endpoint.provider}-server`,
      hostname: endpoint.endpoint.split(':')[0],
      port: endpoint.endpoint.includes(':') ? parseInt(endpoint.endpoint.split(':')[1], 10) : 3000,
      protocol: 'http',
    });
    setAuthType('bearer_token');
    setShowAdvancedAuth(false);
    setAuthFields({ token: '', username: '', password: '', jwtSecret: '', jwtAlgorithm: 'HS256', clientId: '', clientSecret: '', tokenUrl: '', scope: '' });
    setOnboardError(null);
    setShowAddMcpModal(true);
  };

  const credentialsForAuthType = (): Record<string, any> | undefined => {
    switch (authType) {
      case 'none':
        return undefined;
      case 'basic':
        return { username: authFields.username, password: authFields.password };
      case 'bearer_token':
      case 'api_key':
        return { token: authFields.token };
      case 'jwt':
        return { secret: authFields.jwtSecret, algorithm: authFields.jwtAlgorithm };
      case 'oauth2':
        return { client_id: authFields.clientId, client_secret: authFields.clientSecret, token_url: authFields.tokenUrl, scope: authFields.scope || undefined };
      default:
        return undefined;
    }
  };

  const addDiscoveredMcp = async () => {
    if (!mcpFormData.name || !mcpFormData.hostname || !mcpFormData.port) {
      flash('Name, hostname, and port are required');
      return;
    }
    setOnboarding(true);
    setOnboardError(null);
    try {
      const res = await fetch(`${apiUrl}/admin/mcp/onboard-suggested`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: mcpFormData.name,
          hostname: mcpFormData.hostname,
          port: mcpFormData.port,
          protocol: mcpFormData.protocol,
          auth_type: authType,
          credentials: credentialsForAuthType(),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        // Validation (422) or registration failure — keep the modal open with
        // the entered auth/port intact so the admin can fix and retry, rather
        // than losing the form and re-onboarding from scratch.
        setOnboardError(data.message || data.error || 'Onboarding failed');
        return;
      }
      flash(`MCP server "${mcpFormData.name}" onboarded — ${data.tools_count ?? 0} tool(s) discovered`);
      setShowAddMcpModal(false);
      setSelectedEndpoint(null);
      await loadAll();
    } catch (err: any) {
      setOnboardError(err.message);
    } finally {
      setOnboarding(false);
    }
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const hosts = scanHosts.split(/[\s,]+/).filter(Boolean);
      const ports = scanPorts.split(/[\s,]+/).filter(Boolean).map((p) => parseInt(p, 10));
      const res = await fetch(`${apiUrl}/admin/mcp/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hosts, ports }),
      }).then((r) => r.json());
      flash(`Scan complete: probed ${res.scanned}, found ${res.found?.length || 0} MCP server(s).`);
      setShowScan(false);
      await loadAll();
    } catch (err: any) {
      flash(`Scan failed: ${err.message}`);
    } finally {
      setScanning(false);
    }
  };

  const runIngest = async () => {
    setIngesting(true);
    try {
      const res = await fetch(`${apiUrl}/admin/discovery/ingest-logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ format: ingestFormat, text: ingestText }),
      }).then((r) => r.json());
      flash(`Ingested: ${res.matched}/${res.parsed} lines matched · ${res.agents_upserted} agent(s), ${res.endpoints_upserted} endpoint(s).`);
      setShowIngest(false);
      setIngestText('');
      await loadAll();
    } catch (err: any) {
      flash(`Ingest failed: ${err.message}`);
    } finally {
      setIngesting(false);
    }
  };

  const onboard = async (kind: 'mcp' | 'agent', id: string, name?: string) => {
    setBusy(id);
    try {
      await fetch(`${apiUrl}/admin/discovery/onboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, id, name }),
      });
      flash('Brought under Agenti5 management.');
      await loadAll();
    } catch (err: any) {
      flash(`Onboard failed: ${err.message}`);
    } finally {
      setBusy(null);
    }
  };

  /** Suggested name for an agent about to be adopted — the readable parts of
   *  its attribution, never the discovery hash. */
  const suggestAgentName = (a: DiscoveredAgent): string => {
    const host = a.source_host || a.source_ip;
    if (a.process_name && host) return `${a.process_name} · ${host}`;
    return a.process_name || host || '';
  };

  const confirmAdopt = async () => {
    if (!adopting) return;
    const name = adoptName.trim();
    if (!name) return;
    const target = adopting;
    setAdopting(null);
    await onboard('agent', target.disc_id, name);
  };

  const deleteDiscovered = async (kind: 'agent' | 'endpoint', id: string, label: string) => {
    if (!window.confirm(`Delete discovered ${kind} "${label}"? This also stops it from being re-detected by future log ingestion or the ai-sensor.`)) return;
    setBusy(id);
    try {
      await fetch(`${apiUrl}/admin/discovery/${kind === 'agent' ? 'agents' : 'endpoints'}/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
      flash('Deleted from Discovery.');
      await loadAll();
    } catch (err: any) {
      flash(`Delete failed: ${err.message}`);
    } finally {
      setBusy(null);
    }
  };

  const markNotAi = async (endpoint: string) => {
    if (!window.confirm(`Mark "${endpoint}" as not AI? It will stop being classified as shadow-AI everywhere (blocklist, cost, discovery) until this is cleared.`)) return;
    setBusy(endpoint);
    try {
      await markEndpointNotAi(apiUrl, endpoint);
      flash('Marked not AI.');
      await loadAll();
    } catch (err: any) {
      flash(`Update failed: ${err.message}`);
    } finally {
      setBusy(null);
    }
  };

  const openConfirmAi = async (e: DiscoveredEndpoint) => {
    setConfirmingAi(e);
    setConfirmProviderId('');
    setConfirmRiskLevel('unsanctioned');
    if (confirmProviders.length === 0) {
      try {
        setConfirmProviders(await listAllProviders(apiUrl));
      } catch {
        // Non-fatal — the modal still lets an admin pick a bare risk level.
      }
    }
  };

  const submitConfirmAi = async () => {
    if (!confirmingAi) return;
    const endpoint = confirmingAi.endpoint;
    setConfirmingAi(null);
    setBusy(endpoint);
    try {
      await confirmEndpointAi(apiUrl, endpoint, confirmProviderId, confirmRiskLevel);
      flash('Confirmed as AI.');
      await loadAll();
    } catch (err: any) {
      flash(`Update failed: ${err.message}`);
    } finally {
      setBusy(null);
    }
  };

  const shadowMcp = servers.filter((s) => s.management_status === 'unmanaged');

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1>
            <Icon name="search" size={20} /> {t('pages.discovery.discovery')}
          </h1>
          <p className="subtitle">{t('pages.discovery.findEveryAgentAndMCPServer')}</p>
        </div>
        <div className="ent-toolbar" style={{ margin: 0 }}>
          <button className="ent-btn primary" onClick={() => setShowScan(true)}>
            <Icon name="search" /> {t('pages.discovery.scanMCP')}
          </button>
          <button className="ent-btn" onClick={() => setShowIngest(true)}>
            <Icon name="logs" /> {t('pages.discovery.ingestLogs')}
          </button>
          <button
            className={`ent-btn ${stats?.queue.enabled ? 'success' : 'warning'}`}
            onClick={() => toggleIngestion()}
            title={stats?.queue.enabled ? t('pages.discovery.pauseLogIngestion') : t('pages.discovery.resumeLogIngestion')}
          >
            <Icon name={stats?.queue.enabled ? 'power' : 'bolt'} />
            {stats?.queue.enabled ? t('pages.discovery.pause') : t('pages.discovery.resume')}
          </button>
          <button className="ent-btn" onClick={loadAll} disabled={loading}>
            <Icon name="refresh" className={loading ? 'spin' : ''} /> {t('pages.discovery.refresh')}
          </button>
        </div>
      </div>

      {error && <div className="ent-empty">{error}</div>}

      {/* Ingestion status badge */}
      {stats && (
        <div style={{ marginBottom: 16, padding: '10px 16px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, display: 'flex', gap: 24, flexWrap: 'wrap', color: 'var(--text-2)' }}>
          <div><span style={{ fontWeight: 600, color: 'var(--text)' }}>{t('pages.discovery.syslog')}</span> · {t('pages.discovery.syslogStats', { received: stats.syslog.received, parsed: stats.syslog.parsed, errors: stats.syslog.errors })}</div>
          <div><span style={{ fontWeight: 600, color: 'var(--text)' }}>{t('pages.discovery.queue')}</span> · {t('pages.discovery.queueStats', { pending: stats.queue.queueDepth, processed: stats.queue.processed, dropped: stats.queue.dropped })}</div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: stats.queue.enabled ? 'var(--success)' : 'var(--danger)', display: 'inline-block' }} />
            <span style={{ fontWeight: 600, color: 'var(--text)' }}>{stats.queue.enabled ? t('pages.discovery.active') : t('pages.discovery.paused')}</span>
          </div>
        </div>
      )}

      {/* Listening URIs & Instructions */}
      <div style={{ marginBottom: 16, padding: '8px 14px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderLeft: '3px solid var(--info)', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', fontSize: 11, color: 'var(--text-2)' }}>
        <span style={{ fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
          <Icon name="signal" size={12} /> {t('pages.discovery.logForwarding')}
        </span>
        <span style={{ color: 'var(--border-strong)' }}>|</span>
        <span><span style={{ color: 'var(--text-2)' }}>{t('pages.discovery.syslog')}</span> <code style={{ fontFamily: 'monospace', background: 'var(--bg)', padding: '1px 5px', borderRadius: 3, border: '1px solid var(--border)', color: 'var(--text)' }}>{window.location.hostname}:514</code></span>
        <span><span style={{ color: 'var(--text-2)' }}>{t('pages.discovery.webhook')}</span> <code style={{ fontFamily: 'monospace', background: 'var(--bg)', padding: '1px 5px', borderRadius: 3, border: '1px solid var(--border)', color: 'var(--text)', fontSize: 10 }}>POST /admin/discovery/ingest-logs-webhook</code></span>
        <span style={{ color: 'var(--border-strong)' }}>|</span>
        <span style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          {['DNS', 'Squid', 'OTel', 'CEF'].map((fmt) => (
            <span key={fmt} style={{ padding: '1px 6px', background: 'color-mix(in srgb, var(--info) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--info) 25%, transparent)', borderRadius: 3, fontSize: 10, fontWeight: 600, color: 'var(--info)' }}>
              {fmt}
            </span>
          ))}
        </span>
      </div>

      {/* Headline: shadow vs managed */}
      <div className="metric-row">
        <div className="metric accent-warning">
          <span className="label">
            <Icon name="warning" /> {t('pages.discovery.shadowAgents')}
          </span>
          <div className="value">{summary?.agents.shadow ?? '—'}</div>
        </div>
        <div className="metric accent-success">
          <span className="label">
            <Icon name="checkBadge" /> {t('pages.discovery.managedAgents')}
          </span>
          <div className="value">{summary?.agents.managed ?? '—'}</div>
        </div>
        <div className="metric accent-warning">
          <span className="label">
            <Icon name="server" /> {t('pages.discovery.unmanagedMCP')}
          </span>
          <div className="value">{summary?.mcp_servers.unmanaged ?? '—'}</div>
        </div>
        <div className="metric accent-info">
          <span className="label">
            <Icon name="bolt" /> {t('pages.discovery.aiEndpointsLLMMCP')}
          </span>
          <div className="value">
            {summary ? `${summary.endpoints.llm} / ${summary.endpoints.mcp}` : '—'}
          </div>
        </div>
        <div className="metric accent-warning">
          <span className="label">
            <Icon name="warning" /> {t('pages.discovery.shadowAIProtected')}
          </span>
          <div className="value">
            {summary?.shadow_ai ? `${summary.shadow_ai.shadow} / ${summary.shadow_ai.protected}` : '—'}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="ent-toolbar" style={{ gap: 8 }}>
        <button className={`ent-btn ${tab === 'agents' ? 'primary' : ''}`} onClick={() => setTab('agents')}>
          <Icon name="users" /> {t('pages.discovery.agentsTab', { count: agents.length })}
        </button>
        <button className={`ent-btn ${tab === 'mcp' ? 'primary' : ''}`} onClick={() => setTab('mcp')}>
          <Icon name="server" /> {t('pages.discovery.mcpServersTab', { count: servers.length })}
        </button>
        <button className={`ent-btn ${tab === 'endpoints' ? 'primary' : ''}`} onClick={() => setTab('endpoints')}>
          <Icon name="bolt" /> {t('pages.discovery.endpointsTab', { count: endpoints.length })}
        </button>
        <button className={`ent-btn ${tab === 'graph' ? 'primary' : ''}`} onClick={() => setTab('graph')}>
          <Icon name="share" /> {t('pages.discovery.graph')}
        </button>
        <button className={`ent-btn ${tab === 'bom' ? 'primary' : ''}`} onClick={() => setTab('bom')}>
          <Icon name="server" /> {t('pages.discovery.aiBOMSpend')}
        </button>
      </div>

      {tab === 'agents' && (
        <div className="ent-card">
          <div className="ent-table-wrap scroll">
            <table className="ent-table">
              <thead>
                <tr>
                  <th>{t('pages.discovery.sourceHostIPUser')}</th>
                  <th>{t('pages.discovery.destinations')}</th>
                  <th style={{ width: 90 }}>{t('pages.discovery.type')}</th>
                  <th style={{ width: 90 }}>{t('pages.discovery.requests')}</th>
                  <th style={{ width: 100 }}>{t('pages.discovery.status')}</th>
                  <th style={{ width: 110 }}>{t('pages.discovery.origin')}</th>
                  <th style={{ width: 150, textAlign: 'right' }}>{t('pages.discovery.action')}</th>
                </tr>
              </thead>
              <tbody>
                {agents.length === 0 && (
                  <tr><td colSpan={7} className="ent-empty">{t('pages.discovery.noAgentsDiscoveredYetRunA')}</td></tr>
                )}
                {agents.map((a) => {
                  let dests: string[] = [];
                  try { dests = JSON.parse(a.dest_endpoints || '[]'); } catch { /* ignore */ }
                  return (
                    <tr key={a.disc_id}>
                      <td>
                        <strong>{a.display_name || a.source_host || a.source_key}</strong>
                        {(a.source_ip || a.identity_hint || a.process_name) && (
                          <div style={{ fontSize: 10, color: 'var(--text-2)', marginTop: 2 }}>
                            {a.source_ip && <span>{a.source_ip}</span>}
                            {a.identity_hint && <span>{a.source_ip ? ' · ' : ''}{a.identity_hint}</span>}
                            {a.process_name && (
                              <span>
                                {(a.source_ip || a.identity_hint) ? ' · ' : ''}
                                <Icon name="bolt" size={10} /> {a.process_name}
                                {a.device_os ? ` · ${a.device_os}` : ''}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ maxWidth: 320 }}>{dests.join(', ')}</td>
                      <td>{a.endpoint_types}</td>
                      <td>{a.request_count}</td>
                      <td><span className={`badge ${STATUS_BADGE[a.status]}`}>{STATUS_LABEL_KEY[a.status] ? t(STATUS_LABEL_KEY[a.status]) : a.status}</span></td>
                      <td>{a.source}</td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          {a.status === 'shadow' && (
                            <button
                              className="ent-btn primary sm"
                              disabled={busy === a.disc_id}
                              onClick={() => { setAdopting(a); setAdoptName(suggestAgentName(a)); }}
                            >
                              <Icon name="shield" /> {t('pages.discovery.bringUnderAgenti5')}
                            </button>
                          )}
                          <button
                            className="ent-btn sm"
                            disabled={busy === a.disc_id}
                            onClick={() => deleteDiscovered('agent', a.disc_id, a.source_host || a.source_key)}
                            title={t('pages.discovery.deleteThisDiscoveredAgent')}
                          >
                            <Icon name="trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'mcp' && (
        <div className="ent-card">
          <div className="ent-card-head">
            <span>{t('pages.discovery.unmanagedOfMCPServers', { v0: shadowMcp.length, v1: servers.length })}</span>
            {shadowMcp.length === 0 && <span style={{ color: 'var(--success)', fontSize: 12 }}>{t('pages.discovery.allMCPServersUnderManagement')}</span>}
          </div>
          <div className="ent-table-wrap scroll">
            <table className="ent-table">
              <thead>
                <tr>
                  <th>{t('pages.discovery.name')}</th>
                  <th>{t('pages.discovery.address')}</th>
                  <th style={{ width: 100 }}>{t('pages.discovery.origin')}</th>
                  <th style={{ width: 120 }}>{t('pages.discovery.management')}</th>
                  <th style={{ width: 110 }}>{t('pages.discovery.health')}</th>
                  <th style={{ width: 200, textAlign: 'right' }}>{t('pages.discovery.action')}</th>
                </tr>
              </thead>
              <tbody>
                {servers.length === 0 && (
                  <tr><td colSpan={6} className="ent-empty">{t('pages.discovery.noMCPServersKnownRunA')}</td></tr>
                )}
                {servers.map((s) => (
                  <tr key={s.server_id}>
                    <td><strong>{s.name}</strong></td>
                    <td>{s.hostname}:{s.port}</td>
                    <td>{s.source || t('pages.discovery.manual')}</td>
                    <td>
                      <span className={`badge ${s.management_status === 'unmanaged' ? 'badge-error' : 'badge-success'}`}>
                        {s.management_status || t('pages.discovery.managed')}
                      </span>
                    </td>
                    <td>{s.health_status}</td>
                    <td style={{ textAlign: 'right' }}>
                      {s.management_status === 'unmanaged' && (
                        <button className="ent-btn primary sm" disabled={busy === s.server_id} onClick={() => onboard('mcp', s.server_id)}>
                          <Icon name="shield" /> {t('pages.discovery.bringUnderAgenti5')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'endpoints' && (
        <div className="ent-card">
          <div className="ent-table-wrap scroll">
            <table className="ent-table">
              <thead>
                <tr>
                  <th>{t('pages.discovery.endpoint')}</th>
                  <th style={{ width: 160 }}>{t('pages.discovery.provider')}</th>
                  <th style={{ width: 80 }}>{t('pages.discovery.type')}</th>
                  <th style={{ width: 110 }}>{t('pages.discovery.risk')}</th>
                  <th style={{ width: 150 }}>{t('pages.discovery.classification')}</th>
                  <th style={{ width: 90 }}>{t('pages.discovery.scope')}</th>
                  <th style={{ width: 80 }}>{t('pages.discovery.hits')}</th>
                  <th style={{ width: 180, textAlign: 'right' }}>{t('pages.discovery.action')}</th>
                </tr>
              </thead>
              <tbody>
                {endpoints.length === 0 && (
                  <tr><td colSpan={8} className="ent-empty">{t('pages.discovery.noEndpointsDiscoveredYet')}</td></tr>
                )}
                {endpoints.map((e) => (
                  <tr key={e.endpoint}>
                    <td>
                      <strong>{e.endpoint}</strong>
                      {e.source === 'ai-sensor' && (
                        <span className="badge badge-info" style={{ marginLeft: 6, fontSize: 10 }} title={t('pages.discovery.detectedByAiSensorAppliance')}>
                          <Icon name="signal" size={10} /> {t('pages.discovery.aiSensor')}
                        </span>
                      )}
                    </td>
                    <td>{e.provider}</td>
                    <td><span className={`badge ${e.type === 'mcp' ? 'badge-info' : 'badge-redact'}`}>{e.type}</span></td>
                    <td>
                      {e.riskLevel && (
                        <span className={`badge ${RISK_BADGE[e.riskLevel] || 'badge-unknown'}`}>
                          {RISK_LABEL[e.riskLevel] || e.riskLevel}
                        </span>
                      )}
                    </td>
                    <td>
                      {e.classification === 'shadow_ai' && <span className="badge badge-error">{t('pages.discovery.shadowAI')}</span>}
                      {e.classification === 'ztai_protected' && <span className="badge badge-success">{t('pages.discovery.agenti5Protected')}</span>}
                      {!e.classification && <span style={{ color: 'var(--text-2)' }}>—</span>}
                      {e.inferred_model && (
                        <div style={{ fontSize: 10, color: 'var(--text-2)', marginTop: 2 }}>{e.inferred_model}</div>
                      )}
                    </td>
                    <td>{e.internal ? t('pages.discovery.internal') : t('pages.discovery.external')}</td>
                    <td>{e.hit_count}</td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        {e.type === 'mcp' && e.internal && (
                          <button className="ent-btn primary sm" onClick={() => openAddMcpModal(e)}>
                            <Icon name="plus" /> {t('pages.discovery.addToManaged')}
                          </button>
                        )}
                        {e.classification === 'shadow_ai' && (
                          <button
                            className="ent-btn sm"
                            disabled={busy === e.endpoint}
                            onClick={() => markNotAi(e.endpoint)}
                            title={t('pages.discovery.notActuallyAIStopClassifyingThis')}
                          >
                            {t('pages.discovery.notAI')}
                          </button>
                        )}
                        {!e.classification && (
                          <button
                            className="ent-btn sm"
                            disabled={busy === e.endpoint}
                            onClick={() => openConfirmAi(e)}
                            title={t('pages.discovery.confirmThisHostAsAnAI')}
                          >
                            {t('pages.discovery.confirmAI')}
                          </button>
                        )}
                        <button
                          className="ent-btn sm"
                          disabled={busy === e.endpoint}
                          onClick={() => deleteDiscovered('endpoint', e.endpoint, e.endpoint)}
                          title={t('pages.discovery.deleteThisDiscoveredEndpoint')}
                        >
                          <Icon name="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'graph' && <ShadowAiGraph apiUrl={apiUrl} />}

      {tab === 'bom' && (
        <>
          {bomLoading && <div className="ent-card" style={{ padding: 16 }}>{t('pages.discovery.loading')}</div>}

          {!bomLoading && spend && !spend.configured && (
            <div className="ent-card" style={{ padding: 16, color: 'var(--text-2)' }}>
              <Icon name="warning" /> {spend.message}
            </div>
          )}

          {!bomLoading && spend?.configured && (
            <div className="ent-card" style={{ padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
                  <Icon name="warning" /> {t('pages.discovery.estimatedShadowAISpendExposure')}
                </span>
                <span style={{ fontSize: 28, fontWeight: 700 }}>
                  ${spend.total_estimated_cost_usd.toFixed(2)}
                </span>
                <button
                  className="ent-btn sm"
                  style={{ marginLeft: 'auto' }}
                  onClick={() => downloadAuthed('/admin/discovery/shadow-ai-spend/export.pdf', `ztai-ai-cost-report-${new Date().toISOString().slice(0, 10)}.pdf`)}
                >
                  <Icon name="download" /> {t('pages.discovery.downloadPDF')}
                </button>
              </div>
              {spend.caveat && (
                <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 6 }}>{spend.caveat}</div>
              )}
              {spend.by_provider.length > 0 && (
                <div className="ent-table-wrap" style={{ marginTop: 12 }}>
                  <table className="ent-table">
                    <thead>
                      <tr>
                        <th>{t('pages.discovery.provider')}</th>
                        <th style={{ width: 100 }}>{t('pages.discovery.events')}</th>
                        <th style={{ width: 160 }}>{t('pages.discovery.estPromptTokens')}</th>
                        <th style={{ width: 120 }}>{t('pages.discovery.estCostUSD')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {spend.by_provider.map((p) => (
                        <tr key={p.provider}>
                          <td>{p.provider}</td>
                          <td>{p.event_count}</td>
                          <td>{p.estimated_prompt_tokens.toLocaleString()}</td>
                          <td>${p.estimated_cost_usd.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {!bomLoading && (
            <div className="ent-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px' }}>
                <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
                  {t('pages.discovery.bomEntries', { count: bomEntries.length })}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="ent-btn sm"
                    onClick={() => downloadAuthed('/admin/discovery/ai-bom/export.csv', `ztai-ai-bom-${new Date().toISOString().slice(0, 10)}.csv`)}
                  >
                    <Icon name="download" /> {t('pages.discovery.exportCSV')}
                  </button>
                  <button
                    className="ent-btn sm"
                    onClick={() => downloadAuthed('/admin/discovery/ai-bom/export.pdf', `ztai-ai-bom-${new Date().toISOString().slice(0, 10)}.pdf`)}
                  >
                    <Icon name="download" /> {t('pages.discovery.downloadPDF')}
                  </button>
                </div>
              </div>
              <div className="ent-table-wrap scroll">
                <table className="ent-table">
                  <thead>
                    <tr>
                      <th>{t('pages.discovery.vendor')}</th>
                      <th>{t('pages.discovery.service')}</th>
                      <th style={{ width: 80 }}>{t('pages.discovery.type')}</th>
                      <th style={{ width: 110 }}>{t('pages.discovery.risk')}</th>
                      <th style={{ width: 110 }}>{t('pages.discovery.status')}</th>
                      <th style={{ width: 150 }}>{t('pages.discovery.classification')}</th>
                      <th style={{ width: 140 }}>{t('pages.discovery.firstSeen')}</th>
                      <th style={{ width: 140 }}>{t('pages.discovery.lastSeen')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bomEntries.length === 0 && (
                      <tr><td colSpan={8} className="ent-empty">{t('pages.discovery.noAIVendorsDiscoveredYet')}</td></tr>
                    )}
                    {bomEntries.map((e) => (
                      <tr key={e.service}>
                        <td>{e.vendor}</td>
                        <td>{e.service}</td>
                        <td><span className={`badge ${e.type === 'mcp' ? 'badge-info' : 'badge-redact'}`}>{e.type}</span></td>
                        <td>
                          {e.risk_level && (
                            <span className={`badge ${RISK_BADGE[e.risk_level] || 'badge-unknown'}`}>
                              {RISK_LABEL[e.risk_level] || e.risk_level}
                            </span>
                          )}
                        </td>
                        <td>{e.management_status}</td>
                        <td>
                          {e.classification === 'shadow_ai' && <span className="badge badge-error">{t('pages.discovery.shadowAI')}</span>}
                          {e.classification === 'ztai_protected' && <span className="badge badge-success">{t('pages.discovery.agenti5Protected')}</span>}
                          {!e.classification && <span style={{ color: 'var(--text-2)' }}>—</span>}
                        </td>
                        <td>{e.first_seen}</td>
                        <td>{e.last_seen}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {adopting && (
        <Modal
          title={t('pages.discovery.bringAgentUnderAgenti5')}
          icon="shield"
          maxWidth={460}
          onClose={() => setAdopting(null)}
          footer={
            <>
              <button className="ent-btn" onClick={() => setAdopting(null)}>{t('pages.discovery.cancel')}</button>
              <button className="ent-btn primary" disabled={!adoptName.trim()} onClick={confirmAdopt}>
                <Icon name="shield" /> {t('pages.discovery.bringUnderAgenti5')}
              </button>
            </>
          }
        >
          <p style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.6, marginTop: 0 }}>
            {t('pages.discovery.giveThisAgentANameYou')}
          </p>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-2)', marginBottom: 6 }}>
            {t('pages.discovery.agentName')}
          </label>
          <input
            autoFocus
            value={adoptName}
            maxLength={120}
            onChange={(e) => setAdoptName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && adoptName.trim() && confirmAdopt()}
            placeholder={t('pages.discovery.eGOllamaOnJameelsLaptop')}
            style={{
              width: '100%',
              padding: '8px 10px',
              fontSize: 13,
              borderRadius: 6,
              border: '1px solid var(--border)',
              background: 'var(--bg)',
              color: 'var(--text)',
              outline: 'none',
            }}
          />
          <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-2)' }}>
            <div style={{ fontWeight: 700, marginBottom: 3 }}>{t('pages.discovery.discoveredAs')}</div>
            <code style={{ wordBreak: 'break-all', fontSize: 10.5 }}>{adopting.source_key}</code>
          </div>
        </Modal>
      )}

      {confirmingAi && (
        <Modal
          title={t('pages.discovery.confirmAsAI')}
          icon="shield"
          maxWidth={440}
          onClose={() => setConfirmingAi(null)}
          footer={
            <>
              <button className="ent-btn" onClick={() => setConfirmingAi(null)}>{t('pages.discovery.cancel')}</button>
              <button className="ent-btn primary" onClick={submitConfirmAi}>
                <Icon name="shield" /> {t('pages.discovery.confirmAI')}
              </button>
            </>
          }
        >
          <p style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.6, marginTop: 0 }}>
            {t('pages.discovery.tag')} <code style={{ wordBreak: 'break-all' }}>{confirmingAi.endpoint}</code> {t('pages.discovery.asAnAIEndpointSoIt')}
          </p>
          <div className="ent-field">
            <label>{t('pages.discovery.providerOptionalLeaveBlankForA')}</label>
            <Select value={confirmProviderId} onValueChange={setConfirmProviderId}>
              <SelectItem value="">{t('pages.discovery.none')}</SelectItem>
              {confirmProviders.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
              ))}
            </Select>
          </div>
          <div className="ent-field">
            <label>{t('pages.discovery.riskLevel')}</label>
            <Select value={confirmRiskLevel} onValueChange={(v: string) => setConfirmRiskLevel(v as RiskLevel)}>
              <SelectItem value="sanctioned">{t('pages.discovery.sanctioned')}</SelectItem>
              <SelectItem value="tolerated">{t('pages.discovery.tolerated')}</SelectItem>
              <SelectItem value="unsanctioned">{t('pages.discovery.unsanctioned')}</SelectItem>
              <SelectItem value="high_risk">{t('pages.discovery.highRisk')}</SelectItem>
            </Select>
          </div>
        </Modal>
      )}

      {showScan && (
        <Modal title={t('pages.discovery.activeMCPScan')} icon="search" onClose={() => setShowScan(false)}
          footer={
            <>
              <button className="ent-btn" onClick={() => setShowScan(false)}>{t('pages.discovery.cancel')}</button>
              <button className="ent-btn primary" disabled={scanning} onClick={runScan}>
                <Icon name="search" className={scanning ? 'spin' : ''} /> {scanning ? t('pages.discovery.scanning') : t('pages.discovery.scan')}
              </button>
            </>
          }>
          <p className="subtitle">{t('pages.discovery.probeAnAuthorizedHostPortScope')}</p>
          <div className="ent-field">
            <label>{t('pages.discovery.hostsCommaOrSpaceSeparated')}</label>
            <input className="ent-input" value={scanHosts} onChange={(e) => setScanHosts(e.target.value)} placeholder={t('pages.discovery.localhost10005')} />
          </div>
          <div className="ent-field">
            <label>{t('pages.discovery.ports')}</label>
            <input className="ent-input" value={scanPorts} onChange={(e) => setScanPorts(e.target.value)} placeholder="3001,3002,3003" />
          </div>
        </Modal>
      )}

      {showIngest && (
        <Modal title={t('pages.discovery.ingestEgressDNSLogs')} icon="logs" onClose={() => setShowIngest(false)}
          footer={
            <>
              <button className="ent-btn" onClick={() => setShowIngest(false)}>{t('pages.discovery.cancel')}</button>
              <button className="ent-btn primary" disabled={ingesting || !ingestText.trim()} onClick={runIngest}>
                <Icon name="logs" className={ingesting ? 'spin' : ''} /> {ingesting ? t('pages.discovery.ingesting') : t('pages.discovery.ingest')}
              </button>
            </>
          }>
          <p className="subtitle">{t('pages.discovery.pasteProxyDNSOTelLogLines')}</p>
          <div className="ent-field">
            <label>{t('pages.discovery.format')}</label>
            <Select value={ingestFormat} onValueChange={setIngestFormat}>
              <SelectItem value="squid">{t('pages.discovery.proxySquid')}</SelectItem>
              <SelectItem value="dns">{t('pages.discovery.dns')}</SelectItem>
              <SelectItem value="json">{t('pages.discovery.json')}</SelectItem>
              <SelectItem value="otel">{t('pages.discovery.opentelemetryOTLPJSON')}</SelectItem>
              <SelectItem value="azure">{t('pages.discovery.azureFirewallLogAnalytics')}</SelectItem>
              <SelectItem value="generic">{t('pages.discovery.generic')}</SelectItem>
            </Select>
          </div>
          <div className="ent-field">
            <label>{t('pages.discovery.logLines')}</label>
            <textarea className="ent-input" rows={8} value={ingestText} onChange={(e) => setIngestText(e.target.value)}
              placeholder={ingestFormat === 'azure'
                ? t('pages.discovery.sourceip1020114Fqdn')
                : t('pages.discovery.16234567891101000')} />
          </div>
        </Modal>
      )}

      {/* Add Discovered MCP Server Modal */}
      {showAddMcpModal && selectedEndpoint && (
        <Modal
          title={t('pages.discovery.addToManagedTitle', { name: selectedEndpoint.endpoint })}
          icon="plus"
          onClose={() => setShowAddMcpModal(false)}
          footer={
            <>
              <button className="ent-btn" onClick={() => setShowAddMcpModal(false)}>{t('pages.discovery.cancel')}</button>
              <button className="ent-btn primary" disabled={onboarding} onClick={addDiscoveredMcp}>
                <Icon name="checkCircle" className={onboarding ? 'spin' : ''} /> {onboarding ? t('pages.discovery.validating') : t('pages.discovery.onboardServer')}
              </button>
            </>
          }
        >
          <p className="subtitle">
            {t('pages.discovery.configureThisDiscoveredMCPServerAs')}
          </p>
          {selectedEndpoint.source === 'ai-sensor' && (
            <span className="badge badge-info" style={{ marginBottom: 10, display: 'inline-block' }}>
              <Icon name="signal" size={12} /> {t('pages.discovery.suggestedByAiSensor')}
            </span>
          )}
          {onboardError && (
            <div style={{ padding: 10, marginBottom: 12, background: 'color-mix(in srgb, var(--danger) 12%, transparent)', border: '1px solid var(--danger)', borderRadius: 4, fontSize: 12, color: 'var(--danger)' }}>
              <strong>{t('pages.discovery.onboardingFailed')}</strong> {onboardError}
            </div>
          )}

          <div className="ent-field">
            <label>{t('pages.discovery.serverName')}</label>
            <input
              className="ent-input"
              value={mcpFormData.name}
              onChange={(e) => setMcpFormData({ ...mcpFormData, name: e.target.value })}
              placeholder={t('pages.discovery.myMcpServer')}
            />
          </div>

          <div className="ent-field">
            <label>{t('pages.discovery.hostname')}</label>
            <input
              className="ent-input"
              value={mcpFormData.hostname}
              onChange={(e) => setMcpFormData({ ...mcpFormData, hostname: e.target.value })}
              placeholder={t('pages.discovery.localhostOr19216811')}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="ent-field">
              <label>{t('pages.discovery.port')}</label>
              <input
                className="ent-input"
                type="number"
                value={mcpFormData.port}
                onChange={(e) => setMcpFormData({ ...mcpFormData, port: parseInt(e.target.value) })}
                placeholder="3000"
              />
            </div>

            <div className="ent-field">
              <label>{t('pages.discovery.protocol')}</label>
              <select
                className="ent-select"
                value={mcpFormData.protocol}
                onChange={(e) => setMcpFormData({ ...mcpFormData, protocol: e.target.value as 'http' | 'https' })}
              >
                <option value="http">{t('pages.discovery.http')}</option>
                <option value="https">{t('pages.discovery.https')}</option>
              </select>
            </div>
          </div>

          <div className="ent-field">
            <label>{t('pages.discovery.authentication')}</label>
            <Select value={authType} onValueChange={(v: string) => setAuthType(v as McpAuthType)}>
              <SelectItem value="bearer_token">{t('pages.discovery.bearerToken')}</SelectItem>
              <SelectItem value="none">{t('pages.discovery.none2')}</SelectItem>
              <SelectItem value="api_key">{t('pages.discovery.apiKey')}</SelectItem>
              <SelectItem value="basic">{t('pages.discovery.basicUsernamePassword')}</SelectItem>
            </Select>
          </div>

          {(authType === 'bearer_token' || authType === 'api_key') && (
            <div className="ent-field">
              <label>{authType === 'api_key' ? t('pages.discovery.apiKey') : t('pages.discovery.bearerToken')}</label>
              <input
                className="ent-input"
                type="password"
                value={authFields.token}
                onChange={(e) => setAuthFields({ ...authFields, token: e.target.value })}
                placeholder={authType === 'api_key' ? t('pages.discovery.xAPIKeyValue') : t('pages.discovery.eyjhbgcioi')}
              />
            </div>
          )}

          {authType === 'basic' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="ent-field">
                <label>{t('pages.discovery.username')}</label>
                <input className="ent-input" value={authFields.username} onChange={(e) => setAuthFields({ ...authFields, username: e.target.value })} />
              </div>
              <div className="ent-field">
                <label>{t('pages.discovery.password')}</label>
                <input className="ent-input" type="password" value={authFields.password} onChange={(e) => setAuthFields({ ...authFields, password: e.target.value })} />
              </div>
            </div>
          )}

          <div style={{ marginTop: 4, marginBottom: 8 }}>
            <button type="button" className="ent-btn sm" onClick={() => setShowAdvancedAuth(!showAdvancedAuth)}>
              <Icon name={showAdvancedAuth ? 'chevronDown' : 'chevronRight'} size={12} /> {t('pages.discovery.advancedJWTOAuth2')}
            </button>
          </div>

          {showAdvancedAuth && (
            <>
              <div className="ent-field">
                <label>{t('pages.discovery.advancedAuthType')}</label>
                <Select value={authType === 'jwt' || authType === 'oauth2' ? authType : 'jwt'} onValueChange={(v: string) => setAuthType(v as McpAuthType)}>
                  <SelectItem value="jwt">{t('pages.discovery.jwtSelfIssued')}</SelectItem>
                  <SelectItem value="oauth2">{t('pages.discovery.oauth2ClientCredentials')}</SelectItem>
                </Select>
              </div>
              {authType === 'jwt' && (
                <>
                  <div className="ent-field">
                    <label>{t('pages.discovery.signingSecret')}</label>
                    <input className="ent-input" type="password" value={authFields.jwtSecret} onChange={(e) => setAuthFields({ ...authFields, jwtSecret: e.target.value })} />
                  </div>
                  <div className="ent-field">
                    <label>{t('pages.discovery.algorithm')}</label>
                    <select className="ent-select" value={authFields.jwtAlgorithm} onChange={(e) => setAuthFields({ ...authFields, jwtAlgorithm: e.target.value as 'HS256' | 'RS256' })}>
                      <option value="HS256">{t('pages.discovery.hs256')}</option>
                      <option value="RS256">{t('pages.discovery.rs256')}</option>
                    </select>
                  </div>
                </>
              )}
              {authType === 'oauth2' && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div className="ent-field">
                      <label>{t('pages.discovery.clientID')}</label>
                      <input className="ent-input" value={authFields.clientId} onChange={(e) => setAuthFields({ ...authFields, clientId: e.target.value })} />
                    </div>
                    <div className="ent-field">
                      <label>{t('pages.discovery.clientSecret')}</label>
                      <input className="ent-input" type="password" value={authFields.clientSecret} onChange={(e) => setAuthFields({ ...authFields, clientSecret: e.target.value })} />
                    </div>
                  </div>
                  <div className="ent-field">
                    <label>{t('pages.discovery.tokenURL')}</label>
                    <input className="ent-input" value={authFields.tokenUrl} onChange={(e) => setAuthFields({ ...authFields, tokenUrl: e.target.value })} placeholder={t('pages.discovery.httpsIdpInternalOauthToken')} />
                  </div>
                  <div className="ent-field">
                    <label>{t('pages.discovery.scopeOptional')}</label>
                    <input className="ent-input" value={authFields.scope} onChange={(e) => setAuthFields({ ...authFields, scope: e.target.value })} />
                  </div>
                </>
              )}
            </>
          )}

          <div style={{ padding: 10, backgroundColor: '#e8f5e9', borderRadius: 4, fontSize: 12, marginTop: 12 }}>
            <strong>{t('pages.discovery.endpointInfo')}</strong>
            <div style={{ marginTop: 8, lineHeight: 1.5 }}>
              <div><strong>{t('pages.discovery.provider2')}</strong> {selectedEndpoint.provider}</div>
              <div><strong>{t('pages.discovery.type2')}</strong> {selectedEndpoint.type}</div>
              <div><strong>{t('pages.discovery.scope2')}</strong> {selectedEndpoint.internal ? t('pages.discovery.internalOnPrem') : t('pages.discovery.external2')}</div>
              <div><strong>{t('pages.discovery.hitCount')}</strong> {selectedEndpoint.hit_count}</div>
            </div>
          </div>
        </Modal>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 1000,
          maxWidth: 380,
          padding: '12px 16px',
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 500,
          lineHeight: 1.45,
          background: 'var(--bg-2)',
          color: 'var(--text)',
          border: '1px solid var(--border-strong)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.18), 0 1px 4px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <span style={{ flexShrink: 0, width: 8, height: 8, borderRadius: '50%', background: 'var(--success)', display: 'inline-block' }} />
          {toast}
        </div>
      )}
    </div>
  );
};

export default Discovery;
